import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, Search, Sparkles, PenTool, MessageSquare, Image, 
  Mic, BarChart2, Video, FileText, Bot, Megaphone, ChevronRight,
  Loader2, Star, Clock, Zap, Globe, Download, ExternalLink,
  ImageIcon, VideoIcon, Volume2, FileSpreadsheet, Presentation,
  RefreshCw, History, ChevronDown, Lock
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import ModuleDemoVideo from '../components/ModuleDemoVideo';
import { toast } from 'sonner';
import useEntitlements, { AI_TOOL_ADDON_IDS, moduleForAiTool } from '../hooks/useEntitlements';

const API = process.env.REACT_APP_BACKEND_URL;

const AI_TOOLS = {
  content: {
    name: "Content",
    icon: PenTool,
    color: "from-blue-500 to-cyan-500",
    tools: [
      { id: "writer", name: "AI Writer", description: "Generate blog posts, articles, and content", icon: "✍️", popular: true },
      { id: "editor", name: "AI Editor", description: "Edit and improve your existing content", icon: "📝" },
      { id: "rewriter", name: "AI ReWriter", description: "Rewrite content in different styles", icon: "🔄" },
      { id: "article-wizard", name: "AI Article Wizard", description: "Generate full articles with structure", icon: "📰", popular: true }
    ]
  },
  chat: {
    name: "Chat",
    icon: MessageSquare,
    color: "from-violet-500 to-purple-500",
    tools: [
      { id: "chat", name: "AI Chat", description: "Chat with AI assistant", icon: "💬", popular: true },
      { id: "code", name: "AI Code", description: "Generate and explain code", icon: "👨‍💻", popular: true }
    ]
  },
  media: {
    name: "Media",
    icon: Image,
    color: "from-pink-500 to-rose-500",
    tools: [
      { id: "chat-image", name: "AI Chat Image", description: "Generate images through chat", icon: "🖼️" },
      { id: "product-photo", name: "AI Product Photography", description: "Create product photos", icon: "📦" },
      { id: "avatar", name: "AI Avatar", description: "Generate AI avatars", icon: "👤" },
      { id: "vision", name: "AI Vision", description: "Analyze and describe images", icon: "👁️" }
    ]
  },
  audio: {
    name: "Audio",
    icon: Mic,
    color: "from-orange-500 to-amber-500",
    tools: [
      { id: "voiceover", name: "AI Voiceover", description: "Generate voice narration", icon: "🎙️", popular: true },
      { id: "speech-to-text", name: "AI Speech to Text", description: "Transcribe audio to text", icon: "📜" }
    ]
  },
  analysis: {
    name: "Analysis",
    icon: BarChart2,
    color: "from-green-500 to-emerald-500",
    tools: [
      { id: "detector", name: "AI Detector", description: "Detect AI-generated content", icon: "🔍" },
      { id: "plagiarism", name: "AI Plagiarism", description: "Check content for plagiarism", icon: "✅" }
    ]
  },
  video: {
    name: "Video",
    icon: Video,
    color: "from-red-500 to-pink-500",
    tools: [
      { id: "youtube", name: "AI YouTube", description: "YouTube content optimization", icon: "▶️" }
    ]
  },
  documents: {
    name: "Documents",
    icon: FileText,
    color: "from-indigo-500 to-blue-500",
    tools: [
      { id: "file-chat", name: "AI File Chat", description: "Chat with your documents", icon: "📄", popular: true },
      { id: "web-chat", name: "AI Web Chat", description: "Research and chat about websites", icon: "🌐" },
      { id: "rss", name: "AI RSS", description: "Generate content from RSS feeds", icon: "📡" },
      { id: "presentation", name: "AI Presentation", description: "Generate beautiful PPTX slide decks", icon: "📊", popular: true, badge: "New" }
    ]
  },
  bots: {
    name: "Bots",
    icon: Bot,
    color: "from-teal-500 to-cyan-500",
    tools: [
      { id: "smart-bot", name: "AI Smart Bots", description: "Create custom chatbots", icon: "🤖", popular: true },
      { id: "voice-bot", name: "AI Voice Bot", description: "Build voice chatbots", icon: "📞" }
    ]
  },
  marketing: {
    name: "Marketing",
    icon: Megaphone,
    color: "from-yellow-500 to-orange-500",
    tools: [
      { id: "social-media", name: "AI Social Media", description: "Opens AI Ads social scheduler", icon: "📱", popular: true },
      { id: "brand-voice", name: "Brand Voice", description: "Define your brand tone", icon: "🎯" }
    ]
  }
};

// ---------------------------------------------------------------------------
// Activity feed helpers
// ---------------------------------------------------------------------------
const TOOL_META = {
  media_studio:    { label: 'Media Studio',        icon: Zap,              color: 'text-violet-400  bg-violet-400/10'  },
  presentation:    { label: 'Presentation',        icon: Presentation,     color: 'text-orange-400  bg-orange-400/10'  },
  writer:          { label: 'AI Writer',            icon: PenTool,          color: 'text-blue-400    bg-blue-400/10'    },
  editor:          { label: 'AI Editor',            icon: PenTool,          color: 'text-cyan-400    bg-cyan-400/10'    },
  rewriter:        { label: 'AI ReWriter',          icon: RefreshCw,        color: 'text-teal-400    bg-teal-400/10'    },
  article_wizard:  { label: 'Article Wizard',       icon: FileText,         color: 'text-green-400   bg-green-400/10'   },
  chat:            { label: 'AI Chat',              icon: MessageSquare,    color: 'text-indigo-400  bg-indigo-400/10'  },
  code:            { label: 'AI Code',              icon: FileText,         color: 'text-emerald-400 bg-emerald-400/10' },
  voiceover:       { label: 'AI Voiceover',         icon: Mic,              color: 'text-orange-400  bg-orange-400/10'  },
  speech_to_text:  { label: 'Speech to Text',      icon: Volume2,          color: 'text-yellow-400  bg-yellow-400/10'  },
  image:           { label: 'AI Image',             icon: ImageIcon,        color: 'text-pink-400    bg-pink-400/10'    },
  video:           { label: 'AI Video',             icon: VideoIcon,        color: 'text-purple-400  bg-purple-400/10'  },
  social_media:    { label: 'Social Media',         icon: Megaphone,        color: 'text-rose-400    bg-rose-400/10'    },
  plagiarism:      { label: 'Plagiarism Checker',   icon: Search,           color: 'text-amber-400   bg-amber-400/10'   },
  detector:        { label: 'AI Detector',          icon: Search,           color: 'text-red-400     bg-red-400/10'     },
  youtube:         { label: 'YouTube AI',           icon: VideoIcon,        color: 'text-red-400     bg-red-400/10'     },
  translate:       { label: 'AI Translate',         icon: Globe,            color: 'text-green-400   bg-green-400/10'   },
  summarizer:      { label: 'AI Summarizer',        icon: FileText,         color: 'text-blue-400    bg-blue-400/10'    },
  product_photo:   { label: 'Product Photo',        icon: ImageIcon,        color: 'text-pink-400    bg-pink-400/10'    },
  avatar:          { label: 'AI Avatar',            icon: ImageIcon,        color: 'text-purple-400  bg-purple-400/10'  },
  vision:          { label: 'AI Vision',            icon: ImageIcon,        color: 'text-cyan-400    bg-cyan-400/10'    },
  chat_image:      { label: 'Chat + Image',         icon: ImageIcon,        color: 'text-violet-400  bg-violet-400/10'  },
  file_chat:       { label: 'File Chat',            icon: FileText,         color: 'text-teal-400    bg-teal-400/10'    },
  web_chat:        { label: 'Web Chat',             icon: Globe,            color: 'text-blue-400    bg-blue-400/10'    },
  smart_bot:       { label: 'Smart Bot',            icon: Bot,              color: 'text-violet-400  bg-violet-400/10'  },
  default:         { label: 'AI Tool',              icon: Sparkles,         color: 'text-zinc-400    bg-zinc-400/10'    },
};

const CONTENT_TYPE_ICON = {
  image:    { icon: ImageIcon,        color: 'text-pink-400'   },
  video:    { icon: VideoIcon,        color: 'text-purple-400' },
  audio:    { icon: Volume2,          color: 'text-orange-400' },
  document: { icon: FileSpreadsheet,  color: 'text-orange-400' },
  text:     { icon: FileText,         color: 'text-blue-400'   },
  chat:     { icon: MessageSquare,    color: 'text-indigo-400' },
};

function getInputSummary(input = {}) {
  return (
    input.topic || input.prompt || input.text || input.keyword || input.query ||
    input.product_name || input.brand_name || Object.values(input)[0] || ''
  ).toString().slice(0, 70);
}

function timeAgo(iso) {
  if (!iso) return '';
  const diff = Math.floor((Date.now() - new Date(iso)) / 1000);
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

function getOutputUrl(output = {}, contentType) {
  return output.url || output.download_url || output.audio_url || output.video_url || 
         output.image_url || output.content_url || null;
}

function ActivityItem({ item, onOpen }) {
  const meta = TOOL_META[item.tool_name] || TOOL_META.default;
  const MetaIcon = meta.icon;
  const ctInfo = CONTENT_TYPE_ICON[item.content_type] || CONTENT_TYPE_ICON.text;
  const CtIcon = ctInfo.icon;
  const summary = getInputSummary(item.input || {});
  const outputUrl = getOutputUrl(item.output || {}, item.content_type);
  const isImage = item.content_type === 'image' && outputUrl;
  const isDocument = item.content_type === 'document';

  return (
    <div
      data-testid="activity-item"
      className="flex items-center gap-3 px-4 py-3 rounded-xl transition-all hover:bg-white/5 cursor-pointer group"
      onClick={() => onOpen && onOpen(item)}
    >
      {/* Tool icon */}
      <div className={`flex-shrink-0 w-9 h-9 rounded-lg flex items-center justify-center text-sm ${meta.color}`}>
        <MetaIcon className="w-4 h-4" />
      </div>

      {/* Details */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-xs font-semibold text-white/80">{meta.label}</span>
          <span className={`flex items-center gap-1 text-xs ${ctInfo.color} opacity-60`}>
            <CtIcon className="w-3 h-3" /> {item.content_type}
          </span>
        </div>
        {summary && <p className="text-xs text-white/40 truncate mt-0.5">{summary}</p>}
      </div>

      {/* Thumbnail for images */}
      {isImage && (
        <img
          src={outputUrl.startsWith('http') ? outputUrl : `${API}${outputUrl}`}
          alt=""
          className="w-10 h-10 rounded-lg object-cover flex-shrink-0 border border-white/10"
          onError={e => { e.target.style.display = 'none'; }}
        />
      )}

      {/* Output action */}
      <div className="flex-shrink-0 flex flex-col items-end gap-1">
        <span className="text-xs text-white/25">{timeAgo(item.created_at)}</span>
        {outputUrl && (
          <a
            href={outputUrl.startsWith('http') ? outputUrl : `${API}${outputUrl}`}
            target="_blank"
            rel="noreferrer"
            onClick={e => e.stopPropagation()}
            className={`text-xs flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity ${isDocument ? 'text-orange-400' : 'text-indigo-400'} hover:underline`}
          >
            {isDocument ? <Download className="w-3 h-3" /> : <ExternalLink className="w-3 h-3" />}
            {isDocument ? 'Download' : 'View'}
          </a>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------
const AIToolsDashboard = () => {
  const navigate = useNavigate();
  const { can, entitlements, loading: entitlementsLoading } = useEntitlements();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [recentTools, setRecentTools] = useState([]);

  const [activities, setActivities] = useState([]);
  const [activityLoading, setActivityLoading] = useState(false);
  const [activityTotal, setActivityTotal] = useState(0);
  const activitySkipRef = useRef(0);
  const ACTIVITY_PAGE_SIZE = 10;

  const isToolLocked = (toolId) => {
    if (entitlementsLoading || !entitlements) return false;
    const moduleId = moduleForAiTool(toolId);
    if (!AI_TOOL_ADDON_IDS.has(moduleId)) return false;
    return !can(moduleId);
  };

  const initRef = useRef(null);
  if (initRef.current == null) {
    initRef.current = true;
    const saved = localStorage.getItem('recentAITools');
    if (saved) {
      try { setRecentTools(JSON.parse(saved)); } catch (parseErr) { console.error(parseErr); }
    }
  }

  useEffect(() => {
    const load = async () => {
      const token = localStorage.getItem('token');
      if (!token) return;
      setActivityLoading(true);
      try {
        const res = await fetch(`${API}/api/ai-tools/all-history?limit=${ACTIVITY_PAGE_SIZE}&skip=0`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (!res.ok) return;
        const data = await res.json();
        activitySkipRef.current = ACTIVITY_PAGE_SIZE;
        setActivities(data.items || []);
        setActivityTotal(data.total || 0);
      } catch (err) {
        console.error('Failed to load activity:', err);
      } finally {
        setActivityLoading(false);
      }
    };
    load();
  }, []);

  const refreshActivities = () => {
    activitySkipRef.current = 0;
    const load = async () => {
      const token = localStorage.getItem('token');
      if (!token) return;
      setActivityLoading(true);
      try {
        const res = await fetch(`${API}/api/ai-tools/all-history?limit=${ACTIVITY_PAGE_SIZE}&skip=0`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (!res.ok) return;
        const data = await res.json();
        activitySkipRef.current = ACTIVITY_PAGE_SIZE;
        setActivities(data.items || []);
        setActivityTotal(data.total || 0);
      } catch (err) {
        console.error('Failed to refresh activity:', err);
      } finally {
        setActivityLoading(false);
      }
    };
    load();
  };

  const loadMoreActivities = () => {
    const skip = activitySkipRef.current;
    const load = async () => {
      const token = localStorage.getItem('token');
      if (!token) return;
      setActivityLoading(true);
      try {
        const res = await fetch(`${API}/api/ai-tools/all-history?limit=${ACTIVITY_PAGE_SIZE}&skip=${skip}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (!res.ok) return;
        const data = await res.json();
        activitySkipRef.current = skip + ACTIVITY_PAGE_SIZE;
        setActivities(prev => [...prev, ...(data.items || [])]);
        setActivityTotal(data.total || 0);
      } catch (err) {
        console.error('Failed to load more activity:', err);
      } finally {
        setActivityLoading(false);
      }
    };
    load();
  };

  const handleToolClick = (tool, category) => {
    if (isToolLocked(tool.id)) {
      toast.message(`${tool.name} is a premium add-on`, {
        description: 'Buy it from Snagcart Modules to unlock.',
      });
      navigate('/snagcart/apps', { state: { highlightAddon: moduleForAiTool(tool.id) } });
      return;
    }

    // Save to recent
    const newRecent = [{ ...tool, category }, ...recentTools.filter(t => t.id !== tool.id)].slice(0, 5);
    setRecentTools(newRecent);
    localStorage.setItem('recentAITools', JSON.stringify(newRecent));
    
    // Special routes for dedicated pages
    if (tool.id === 'presentation') {
      navigate('/ai-presentation');
      return;
    }
    
    // Navigate to tool
    navigate(`/ai-tool/${tool.id}`);
  };

  const renderLockBadge = (toolId) => (
    isToolLocked(toolId) ? (
      <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide text-amber-300 bg-amber-500/15 border border-amber-500/30 px-1.5 py-0.5 rounded-full">
        <Lock className="w-3 h-3" /> Add-on
      </span>
    ) : null
  );

  const filteredTools = () => {
    let tools = [];
    
    Object.entries(AI_TOOLS).forEach(([catKey, category]) => {
      if (selectedCategory === 'all' || selectedCategory === catKey) {
        category.tools.forEach(tool => {
          if (!searchQuery || 
              tool.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
              tool.description.toLowerCase().includes(searchQuery.toLowerCase())) {
            tools.push({ ...tool, categoryKey: catKey, categoryName: category.name, categoryColor: category.color });
          }
        });
      }
    });
    
    return tools;
  };

  const popularTools = Object.entries(AI_TOOLS).flatMap(([catKey, category]) => 
    category.tools.filter(t => t.popular).map(t => ({ ...t, categoryKey: catKey, categoryColor: category.color }))
  );

  return (
    <div className="min-h-screen bg-zinc-950 text-white">
      {/* Header */}
      <header className="border-b border-zinc-800 bg-zinc-900/50 backdrop-blur-xl sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/dashboard')}
                className="text-zinc-400 hover:text-white"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Dashboard
              </Button>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center">
                  <Sparkles className="w-5 h-5" />
                </div>
                <div>
                  <h1 className="text-xl font-bold">AI Tools</h1>
                  <p className="text-xs text-zinc-500">22 powerful tools at your fingertips</p>
                </div>
              </div>
            </div>
            
            {/* Search */}
            <div className="relative w-96">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search AI tools..."
                className="pl-10 bg-zinc-800 border-zinc-700 focus:border-violet-500"
              />
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto p-6">
        <ModuleDemoVideo moduleKey="ai-tools" className="mb-6" />
        {/* Quick Stats */}
        <div className="grid grid-cols-4 gap-4 mb-8">
          <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4 flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-violet-500/20 flex items-center justify-center">
              <Zap className="w-6 h-6 text-violet-400" />
            </div>
            <div>
                <p className="text-2xl font-bold">230+</p>
                <p className="text-xs text-zinc-500">AI Models</p>
            </div>
          </div>
          <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4 flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-blue-500/20 flex items-center justify-center">
              <Globe className="w-6 h-6 text-blue-400" />
            </div>
            <div>
              <p className="text-2xl font-bold">9</p>
              <p className="text-xs text-zinc-500">Categories</p>
            </div>
          </div>
          <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4 flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-green-500/20 flex items-center justify-center">
              <Star className="w-6 h-6 text-green-400" />
            </div>
            <div>
              <p className="text-2xl font-bold">{popularTools.length}</p>
              <p className="text-xs text-zinc-500">Popular Tools</p>
            </div>
          </div>
          <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4 flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-orange-500/20 flex items-center justify-center">
              <History className="w-6 h-6 text-orange-400" />
            </div>
            <div>
              <p className="text-2xl font-bold">{activityTotal}</p>
              <p className="text-xs text-zinc-500">Total Generations</p>
            </div>
          </div>
        </div>

        {/* Recent Activity Feed */}
        {!searchQuery && (
          <div className="mb-8">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold flex items-center gap-2">
                <History className="w-5 h-5 text-zinc-400" />
                Recent Activity
                {activityTotal > 0 && (
                  <span className="text-xs text-zinc-500 font-normal ml-1">{activityTotal} total</span>
                )}
              </h2>
              <button
                onClick={() => refreshActivities()}
                className="text-xs text-zinc-500 hover:text-white flex items-center gap-1 transition-colors"
              >
                <RefreshCw className="w-3 h-3" /> Refresh
              </button>
            </div>

            {activityLoading && activities.length === 0 ? (
              <div className="flex items-center justify-center py-8 rounded-xl border border-zinc-800" style={{ background: '#0d0d1f' }}>
                <Loader2 className="w-5 h-5 animate-spin text-zinc-500 mr-2" />
                <span className="text-zinc-500 text-sm">Loading activity...</span>
              </div>
            ) : activities.length === 0 ? (
              <div className="rounded-xl border border-zinc-800 p-8 text-center" style={{ background: '#0d0d1f' }}>
                <History className="w-8 h-8 text-zinc-600 mx-auto mb-2" />
                <p className="text-zinc-500 text-sm">No activity yet. Use any AI tool to see results here!</p>
              </div>
            ) : (
              <div className="rounded-xl border border-zinc-800 overflow-hidden" style={{ background: '#0d0d1f' }}>
                <div className="divide-y divide-zinc-800/50">
                  {activities.map((item, i) => (
                    <ActivityItem
                      key={item.id || i}
                      item={item}
                      onOpen={(a) => {
                        const url = getOutputUrl(a.output || {}, a.content_type);
                        if (url) window.open(url.startsWith('http') ? url : `${API}${url}`, '_blank');
                      }}
                    />
                  ))}
                </div>
                {activities.length < activityTotal && (
                  <div className="px-4 py-3 text-center border-t border-zinc-800/50">
                    <button
                      onClick={loadMoreActivities}
                      disabled={activityLoading}
                      className="text-xs text-zinc-400 hover:text-white flex items-center gap-1.5 mx-auto transition-colors"
                    >
                      {activityLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <ChevronDown className="w-3 h-3" />}
                      Load more ({activityTotal - activities.length} remaining)
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Media Studio Banner */}
        {!searchQuery && selectedCategory === 'all' && (
          <div
            className="mb-8 bg-gradient-to-r from-violet-900/40 via-fuchsia-900/30 to-cyan-900/40 border border-violet-500/20 rounded-2xl p-6 cursor-pointer hover:border-violet-500/50 transition-all"
            onClick={() => navigate('/media-studio')}
            data-testid="media-studio-banner"
          >
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-xs px-2 py-0.5 bg-violet-500/30 text-violet-300 rounded-full font-medium uppercase tracking-wide">New</span>
                  <span className="text-xs text-zinc-500">Powered by Snagcart</span>
                </div>
                <h2 className="text-2xl font-bold text-white mb-1">Media Studio</h2>
                <p className="text-zinc-400 text-sm">230+ AI models — Text to Image, Image to Image, Text to Video, Image to Video & Audio Studio</p>
                <div className="flex gap-3 mt-3">
                  {['Flux', 'Midjourney', 'Kling', 'Sora 2', 'Veo 3', 'Wan 2.6', 'Nano Banana 2'].map(m => (
                    <span key={m} className="text-xs px-2 py-1 bg-white/10 rounded-full text-zinc-300">{m}</span>
                  ))}
                  <span className="text-xs px-2 py-1 bg-white/10 rounded-full text-zinc-500">+ 220 more</span>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="grid grid-cols-2 gap-1.5 opacity-60">
                  {['from-violet-500 to-fuchsia-500','from-cyan-500 to-blue-500','from-pink-500 to-rose-500','from-amber-500 to-orange-500'].map((g, i) => (
                    <div key={i} className={`w-10 h-10 rounded-lg bg-gradient-to-br ${g}`} />
                  ))}
                </div>
                <ChevronRight className="w-6 h-6 text-zinc-400" />
              </div>
            </div>
          </div>
        )}

        {/* Popular Tools */}
        {!searchQuery && selectedCategory === 'all' && (
          <div className="mb-8">
            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Star className="w-5 h-5 text-yellow-400" />
              Popular Tools
            </h2>
            <div className="grid grid-cols-4 gap-4">
              {popularTools.map((tool) => (
                <button
                  key={tool.id}
                  onClick={() => handleToolClick(tool, tool.categoryKey)}
                  className={`relative overflow-hidden rounded-xl p-5 hover:scale-[1.02] transition-all text-left bg-gradient-to-br ${tool.categoryColor} ${isToolLocked(tool.id) ? 'opacity-80' : ''}`}
                >
                  <div className="absolute inset-0 bg-black/20" />
                  <div className="relative">
                    <div className="flex items-start justify-between gap-2 mb-3">
                      <span className="text-3xl block">{tool.icon}</span>
                      {renderLockBadge(tool.id)}
                    </div>
                    <p className="font-semibold text-lg">{tool.name}</p>
                    <p className="text-sm text-white/70 mt-1">{tool.description}</p>
                  </div>
                  <ChevronRight className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/50" />
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Category Filter */}
        <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
          <button
            onClick={() => setSelectedCategory('all')}
            className={`px-4 py-2 rounded-lg whitespace-nowrap transition-all ${
              selectedCategory === 'all'
                ? 'bg-violet-500 text-white'
                : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'
            }`}
          >
            All Tools
          </button>
          {Object.entries(AI_TOOLS).map(([key, category]) => (
            <button
              key={key}
              onClick={() => setSelectedCategory(key)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg whitespace-nowrap transition-all ${
                selectedCategory === key
                  ? 'bg-violet-500 text-white'
                  : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'
              }`}
            >
              <category.icon className="w-4 h-4" />
              {category.name}
            </button>
          ))}
        </div>

        {/* Tools Grid */}
        {searchQuery || selectedCategory !== 'all' ? (
          <div className="grid grid-cols-3 gap-4">
            {filteredTools().map((tool) => (
              <button
                key={tool.id}
                onClick={() => handleToolClick(tool, tool.categoryKey)}
                className={`bg-zinc-900/50 border rounded-xl p-5 hover:bg-zinc-800/50 transition-all text-left group ${
                  isToolLocked(tool.id)
                    ? 'border-amber-500/30 hover:border-amber-500/50'
                    : 'border-zinc-800 hover:border-violet-500/50'
                }`}
              >
                <div className="flex items-start justify-between gap-2">
                  <span className="text-3xl">{tool.icon}</span>
                  <div className="flex flex-col items-end gap-1">
                    <span className={`text-xs px-2 py-1 rounded-full bg-gradient-to-r ${tool.categoryColor} text-white`}>
                      {tool.categoryName}
                    </span>
                    {renderLockBadge(tool.id)}
                  </div>
                </div>
                <p className="font-semibold text-lg mt-3">{tool.name}</p>
                <p className="text-sm text-zinc-400 mt-1">{tool.description}</p>
                <ChevronRight className="w-5 h-5 text-zinc-600 group-hover:text-violet-400 mt-3 transition-colors" />
              </button>
            ))}
          </div>
        ) : (
          // Category Grid View
          <div className="space-y-8">
            {Object.entries(AI_TOOLS).map(([key, category]) => (
              <div key={key}>
                <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                  <category.icon className="w-5 h-5 text-zinc-400" />
                  {category.name}
                  <span className="text-xs text-zinc-500 ml-2">({category.tools.length} tools)</span>
                </h2>
                <div className="grid grid-cols-4 gap-4">
                  {category.tools.map((tool) => (
                    <button
                      key={tool.id}
                      onClick={() => handleToolClick(tool, key)}
                      className={`bg-zinc-900/50 border rounded-xl p-4 hover:bg-zinc-800/50 transition-all text-left group ${
                        isToolLocked(tool.id)
                          ? 'border-amber-500/30 hover:border-amber-500/50'
                          : 'border-zinc-800 hover:border-violet-500/50'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <span className="text-2xl">{tool.icon}</span>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium truncate">{tool.name}</p>
                          <p className="text-xs text-zinc-500 truncate">{tool.description}</p>
                        </div>
                        {isToolLocked(tool.id)
                          ? <Lock className="w-4 h-4 text-amber-400 flex-shrink-0" />
                          : <ChevronRight className="w-4 h-4 text-zinc-600 group-hover:text-violet-400 flex-shrink-0 transition-colors" />}
                      </div>
                      <div className="flex flex-wrap items-center gap-2 mt-2">
                        {tool.popular && (
                          <span className="inline-flex items-center gap-1 text-xs text-yellow-400">
                            <Star className="w-3 h-3" /> Popular
                          </span>
                        )}
                        {tool.badge && (
                          <span className="inline-flex items-center gap-1 text-xs text-emerald-400 bg-emerald-400/10 px-1.5 py-0.5 rounded-full">
                            {tool.badge}
                          </span>
                        )}
                        {renderLockBadge(tool.id)}
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AIToolsDashboard;
